/**
 * Per-destination artwork and URL slugs for the Popular Destinations carousel.
 *
 * WHY THIS IS A SEPARATE FILE
 * ---------------------------
 * The carousel used to be one array holding both the destination NAMES and
 * their pictures. Those names had to match `listings.location` exactly —
 * Destinations/index.js filters with `p.location === state.name`, string
 * equality — so that array was a second, hand-maintained copy of the canonical
 * destination list. A rename in the admin console would not have broken
 * loudly; it would have made every listing in that destination undiscoverable
 * from the homepage while the tile sat there still looking fine.
 *
 * The names now come from `app_config/main.locations`, the document the
 * console writes. This file holds only what config has no opinion about.
 *
 * KEYED BY CANONICAL `value`, never by label. The label is display text an
 * admin may change; the value is what is stored on a listing.
 *
 * `slug` is preserved verbatim rather than derived from the name — 'Mutarazi'
 * has always been served at /destination/mutaridzi-national-park, and
 * generating a slug would quietly change a URL that may be linked to.
 *
 * A destination with no entry here is NOT excluded from anything. The carousel
 * renders text pills, and `slugFor` falls back to a slugify of the name, so
 * every canonical destination appears and every one is addressable. This table
 * now exists for the slugs alone — the images are kept against the day the
 * homepage wants pictures back, and nothing renders them today.
 */
export const DESTINATION_ARTWORK = {
  'Victoria Falls': {
    slug: 'victoria-falls',
    image:
      'https://res.cloudinary.com/pcgwa/image/upload/f_auto,q_auto/v1685645543/vhakacha/301775616_131562829586779_8727183920900492845_n_1',
  },
  'Matobo Hills': {
    slug: 'matobo-hills',
    image:
      'https://res.cloudinary.com/pcgwa/image/upload/f_auto,q_auto/v1685645542/vhakacha/89124550_116766129926513_7165649738372396173_n_1',
  },
  Masvingo: {
    slug: 'masvingo',
    image:
      'https://res.cloudinary.com/pcgwa/image/upload/f_auto,q_auto/v1685645543/vhakacha/278397557_1359712434455919_900984305764561608_n_1',
  },
  Kariba: {
    slug: 'kariba',
    image:
      'https://res.cloudinary.com/pcgwa/image/upload/f_auto,q_auto/v1685646435/vhakacha/327279954_2102134366639701_78491269369945078_n_1',
  },
  Mutarazi: {
    // Not a slugify of the name. Kept exactly as it has always been served.
    slug: 'mutaridzi-national-park',
    image:
      'https://res.cloudinary.com/pcgwa/image/upload/f_auto,q_auto/v1685645542/vhakacha/264439154_264910405529925_5193947319555559453_n_1',
  },
  Chiredzi: {
    slug: 'chiredzi',
    image:
      'https://res.cloudinary.com/pcgwa/image/upload/f_auto,q_auto/v1685645543/vhakacha/Singita-Malilangwe-House-Aerial-View_1',
  },
  Chimanimani: {
    slug: 'chimanimani',
    image:
      'https://res.cloudinary.com/pcgwa/image/upload/f_auto,q_auto/v1685651752/vhakacha/chim_falls',
  },
};

/** @returns {{slug: string, image: string} | undefined} */
export function artworkFor(locationValue) {
  return DESTINATION_ARTWORK[locationValue];
}

/** Lowercase, non-alphanumerics to hyphens, no leading/trailing hyphen. */
function slugify(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The URL segment for a destination.
 *
 * Prefers the artwork slug, which is preserved verbatim precisely BECAUSE it
 * is not always a slugify of the name — 'Mutarazi' is served at
 * /destination/mutaridzi-national-park and has been for as long as the URL has
 * existed. Falling back to a derived slug means a destination with no tile is
 * still addressable; before this, only the seven artworked ones were.
 */
export function slugFor(locationValue) {
  return DESTINATION_ARTWORK[locationValue]?.slug ?? slugify(locationValue);
}

/**
 * Find the destination a URL segment refers to.
 *
 * The destination page used to read its subject from ROUTER STATE
 * (`location.state.name`), set by the carousel tile that linked to it. That
 * made every destination URL unshareable and unrefreshable: arriving with no
 * state left the page with an empty title, which filtered the listings to
 * nothing and rendered a heading with no text under it. It also meant the
 * `:slug` in the route was decorative — nothing ever resolved anything from it.
 *
 * THREE FORMS ARE ACCEPTED, on purpose:
 *
 *   1. the artwork slug            /destination/mutaridzi-national-park
 *   2. a slugify of the name       /destination/nyanga
 *   3. the canonical value itself  /destination/Victoria%20Falls
 *
 * (3) exists because `adHref()` in utils/advertising.js builds a LOCATION
 * advert's link from the canonical value, and that module is mirrored
 * byte-for-behaviour with the provider app's lib/advertising.ts — it cannot
 * reach this file's artwork table without breaking the mirror. Accepting the
 * value here is the cheaper half of that trade, and it also makes any
 * hand-typed URL work.
 *
 * @returns {{value: string, label: string} | null}
 */
export function resolveDestinationSlug(slug, locations) {
  if (!slug) return null;

  const decoded = (() => {
    try {
      return decodeURIComponent(slug);
    } catch (e) {
      // A malformed escape sequence ('%zz') throws. Treat it as literal text
      // rather than failing the whole page.
      return slug;
    }
  })();

  const wanted = decoded.toLowerCase();
  const wantedSlug = slugify(decoded);

  return (
    (locations ?? []).find(
      (l) =>
        slugFor(l.value).toLowerCase() === wanted ||
        slugFor(l.value).toLowerCase() === wantedSlug ||
        String(l.value).toLowerCase() === wanted
    ) ?? null
  );
}

/**
 * Carousel pills: every canonical destination, in the order config lists them.
 *
 * Driven by `locations` — the list read from `app_config/main` — so the admin
 * console controls which destinations appear and in what order.
 *
 * NOT GATED ON ARTWORK. The carousel renders text pills now, so a destination
 * with no picture has nothing missing from it. The old image tiles skipped
 * anything absent from the table above, which quietly hid fourteen of the
 * twenty-one canonical destinations from the homepage. `slugFor` gives each of
 * them an address, and the artwork table is consulted only for the handful of
 * slugs that are not a slugify of the name.
 */
export function destinationPills(locations) {
  return (locations ?? [])
    .filter((location) => location && location.value)
    .map((location) => ({
      // `name` is the canonical value, because the destination page filters
      // listings on it by string equality. Never the label.
      name: location.value,
      // Display text only. An admin may rename this freely.
      label: location.label || location.value,
      link: slugFor(location.value),
      coordinates: location.coordinates,
    }));
}
